import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export const SLATE_CREST_SIZE = 28;
const SLATE_CREST_OVERLAP = -7;
/** The mascot leans out of the gold button's corner (design: 66px on a 40px button). */
export const SLATE_MONKEY_SIZE = 66;
export const SLATE_MONKEY_BUTTON_WIDTH = 122;
const SLATE_BUTTON_HEIGHT = 40;

/**
 * Design 6a warms the slate card from the slate card colour into a deep purple; the end stop is not a
 * theme token (nothing else uses it), so it lives here next to the gradient that needs it.
 */
export const SLATE_GRADIENT_END = '#2A1F3D';

export const getTodayChallengeCardStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      borderRadius: theme.borderRadius.xl,
      padding: 12,
      paddingHorizontal: 14,
      gap: 10,
      overflow: 'hidden',
      shadowColor: theme.shadow,
      shadowOpacity: 0.4,
      shadowRadius: 4,
      shadowOffset: { width: 0, height: 2 },
      elevation: 2,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 8,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    labelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },
    subtitle: {
      fontSize: 11,
      color: theme.gray[500],
    },
    label: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.accent,
      letterSpacing: 1,
      textTransform: 'uppercase',
    },
    newIn: {
      fontSize: 11,
      color: theme.gray[500],
      fontVariant: ['tabular-nums'],
    },
    match: {
      gap: 7,
    },
    matchDivider: {
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: theme.gray[200],
    },
    matchHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    crests: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    crestOverlap: {
      marginLeft: SLATE_CREST_OVERLAP,
    },
    teams: {
      flex: 1,
      minWidth: 0,
      fontSize: 14,
      fontWeight: '700',
      letterSpacing: -0.14,
      color: theme.white,
    },
    vs: {
      color: theme.gray[600],
      fontWeight: '600',
    },
    kickoff: {
      fontSize: 11,
      color: theme.gray[500],
      fontVariant: ['tabular-nums'],
    },
    chips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      height: 26,
      paddingHorizontal: 9,
      borderRadius: theme.borderRadius.sm,
      backgroundColor: theme.background.inset,
    },
    chipLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[500],
    },
    chipValue: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.accentDark,
      fontVariant: ['tabular-nums'],
    },
    buttonsRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 8,
      marginTop: 2,
    },
    sendButton: {
      flex: 1,
      minWidth: 0,
      height: SLATE_BUTTON_HEIGHT,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.primary,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
      shadowColor: theme.primary,
      shadowOpacity: 0.3,
      shadowRadius: 4,
      shadowOffset: { width: 0, height: 2 },
      elevation: 2,
    },
    sendButtonPressed: {
      opacity: 0.85,
      transform: [{ scale: 0.98 }],
    },
    sendButtonHover: {
      opacity: 0.92,
    },
    sendText: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.onPrimary,
    },
    // The mascot overflows the button on purpose; the wrapper keeps that overflow out of the button's clip.
    monkeyButtonWrap: {
      width: SLATE_MONKEY_BUTTON_WIDTH,
      height: SLATE_BUTTON_HEIGHT,
      position: 'relative',
      overflow: 'visible',
    },
    monkeyButton: {
      flex: 1,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      shadowColor: theme.accent,
      shadowOpacity: 0.25,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: 2 },
      elevation: 2,
    },
    monkeyButtonPressed: {
      opacity: 0.9,
      transform: [{ scale: 0.98 }],
    },
    monkeyButtonHover: {
      opacity: 0.95,
    },
    monkeyGradient: {
      flex: 1,
      justifyContent: 'center',
      paddingLeft: 12,
      paddingRight: SLATE_MONKEY_SIZE - 12,
    },
    monkeyText: {
      fontSize: 12,
      fontWeight: '800',
      lineHeight: 13,
      color: theme.textOnCard,
    },
    monkeyMascot: {
      position: 'absolute',
      right: -2,
      bottom: -2,
      width: SLATE_MONKEY_SIZE,
      height: SLATE_MONKEY_SIZE,
    },
    seeAllButton: {
      alignSelf: 'center',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      paddingVertical: 2,
    },
    seeAllPressed: {
      opacity: 0.6,
    },
    seeAllHover: {
      opacity: 0.8,
    },
    seeAllText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[500],
    },
    emptyText: {
      paddingVertical: 6,
      fontSize: 12,
      color: theme.gray[500],
    },
  });

export type TodayChallengeCardStyles = ReturnType<typeof getTodayChallengeCardStyles>;
